import {
    AccountBalanceOutlined,
    CheckCircleRounded,
    CloseRounded,
    EditOutlined,
    InfoOutlined,
    LocalAtm,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useGetBanksQuery } from 'src/_api/bankApi';
import { useRequestWithdrawalMutation } from 'src/_api/mapitShipInvoice';
import { useGetMerchantProfileQuery } from 'src/_api/profileAccountApi';
import { TextFieldNumber } from 'src/_modules/common';
import { joiNumber, runJoiValidate } from 'src/_utils';
import BankAccountDialog from './BankAccountDialog';
import Joi from 'joi';

export default function WithdrawDialog({
    dueAmount
}) {
    const minimumWithdrawalAmount = 100;

    const { t } = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);

    const { data: merchantProfile, refetch: profileRefetch, isLoading } = useGetMerchantProfileQuery({});

    // const getDefaultAmount = () => (dueAmount >= minimumWithdrawalAmount ? String(Number(dueAmount).toFixed(2)) : '');

    const onClose = () => {
        setOpen(false);
    }

    const handleDialogClose = (_, reason) => {
        if (requestingWithdrawalLoading || reason === 'backdropClick') return;
        onClose();
    }

    const [amount, setAmount] = useState('');
    const [validationErrors, setValidationErrors] = useState(null);

    const bankInformation = merchantProfile?.bankInformation;
    const hasBankAccount = !!bankInformation?.iban;

    // Joi schema for amount with custom messages
    const amountSchema = Joi.object({
        amount: joiNumber.number()
            .required()
            .min(minimumWithdrawalAmount)
            .label(t('Amount'))
    })

    const handleAmountChange = ({ target: { value } }) => {
        setAmount(value);
    }

    const [
        requestWithdrawal,
        {
            isLoading: requestingWithdrawalLoading,
            isSuccess: requestingWithdrawalSuccess,
            error: requestingWithdrawalError,
            reset: resetRequestWithdrawal
        }
    ] = useRequestWithdrawalMutation()

    const onConfirm = async () => {
        try {
            setValidationErrors(null)

            const errors = runJoiValidate(amountSchema, { amount });
            if (errors) {
                setValidationErrors(errors);
                return;
            }

            if (!hasBankAccount) return
            await requestWithdrawal({ amount: Number(amount) }).unwrap()

            setAmount('');
            setValidationErrors(null);

            setTimeout(() => {
                resetRequestWithdrawal();
                onClose();
            }, 2500);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (open) {
            setAmount('');
        } else {
            setAmount('');
            setValidationErrors(null);
            resetRequestWithdrawal();
        }
    }, [dueAmount, open, resetRequestWithdrawal]);

    // Bank Handlers
    const { data: banks } = useGetBanksQuery();

    const bank = banks?.find(bank => bank?.id === bankInformation?.bank)
    const bankName = theme?.direction === "rtl" ? bank?.name_ar : bank?.name_en

    const [openBankDialog, setOpenBankDialog] = useState(false);

    const onCloseBankDialog = () => {
        setOpenBankDialog(false);
    }

    const amountHint = `${minimumWithdrawalAmount} - ${Number(dueAmount || 0).toFixed(2)} ${t('Currency')}`;
    const confirmDisabled = requestingWithdrawalSuccess || !amount || dueAmount <= 0 || !hasBankAccount;

    const bankDetails = [
        { label: t('Account Holder Name'), value: bankInformation?.accountName || '--' },
        { label: t('IBAN'), value: bankInformation?.iban || '--' },
        { label: t('Bank'), value: bankName || '--' },
    ];

    const amountFieldStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            fontSize: 18,
            fontWeight: 700,
        },
        '& .MuiInputBase-input': {
            py: 1.65,
        },
        '& .MuiFormHelperText-root': {
            mx: 0,
            mt: 1,
        },
    };

    return (
        <>

            <LoadingButton
                variant='contained'
                onClick={() => setOpen(true)}
                disabled={dueAmount <= 0}
                startIcon={<LocalAtm />}
            >
                {t("Withdraw")}
            </LoadingButton>
            <Dialog
                open={open}
                onClose={handleDialogClose}
                fullWidth
                fullScreen={fullScreen}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: fullScreen ? 0 : 4,
                        overflow: 'hidden',
                        backgroundColor: theme.palette.background.paper,
                    },
                }}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <Box
                        sx={{
                            px: { xs: 2.25, sm: 3 },
                            py: { xs: 2.5, sm: 3 },
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2.5,
                                            display: 'grid',
                                            placeItems: 'center',
                                        }}
                                    >
                                        <LocalAtm sx={{ fontSize: 30 }} />
                                    </Box>

                                    <Typography variant="h5" fontWeight={800} lineHeight={1.1}>
                                        {t("Withdraw")}
                                    </Typography>

                                </Stack>

                                <IconButton
                                    onClick={onClose}
                                    disabled={requestingWithdrawalLoading}
                                >
                                    <CloseRounded />
                                </IconButton>
                            </Stack>

                            {hasBankAccount && (
                                <Typography variant="body2" color="text.secondary">
                                    {t("withdrawMessage")}
                                </Typography>
                            )}

                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    justifyContent="space-between"
                                >
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                                            {t("Available amount in COD wallet")}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={900} lineHeight={1}>
                                            {Number(dueAmount || 0).toFixed(2)}{' '}
                                            <Typography component="span" variant="body1" color="text.secondary" fontWeight={700}>
                                                {t("Currency")}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Box>
                </DialogTitle>
                <BankAccountDialog
                    open={openBankDialog}
                    onClose={onCloseBankDialog}
                    bankInfo={bankInformation}
                    profileRefetch={profileRefetch}
                    profileAccount={merchantProfile}
                />
                <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, py: { xs: 2.25, sm: 3 } }}>
                    <Stack spacing={2.25}>
                        {
                            hasBankAccount &&
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Stack spacing={2}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 46,
                                                    height: 46,
                                                    borderRadius: 2,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    backgroundColor: theme.palette.action.hover,
                                                }}
                                            >
                                                <AccountBalanceOutlined />
                                            </Box>
                                            <Box>
                                                <Typography fontWeight={800}>
                                                    {t("Bank account details")}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EditOutlined />}
                                            onClick={() => setOpenBankDialog(true)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {t("Edit Bank Account")}
                                        </Button>
                                    </Stack>

                                    <Stack spacing={1.25}>
                                        {bankDetails.map((item) => (
                                            <Stack
                                                key={item.label}
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={0.75}
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                justifyContent="space-between"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    //backgroundColor: theme.palette.action.hover,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                                    {item.label}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={800}
                                                    sx={{
                                                        textAlign: { xs: 'left', sm: 'right' },
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {item.value}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Paper>
                        }

                        {
                            hasBankAccount &&
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Stack spacing={1.75}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.25}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <Box>
                                            <Typography variant="h6" fontWeight={800}>
                                                {t("Amount")}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <TextFieldNumber
                                        label={t("Amount")}
                                        variant="outlined"
                                        size="medium"
                                        required={true}
                                        decimal={true}
                                        max={dueAmount}
                                        value={amount}
                                        onChange={handleAmountChange}
                                        error={!!validationErrors?.amount}
                                        helperText={validationErrors?.amount || amountHint}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                                                    {t('Currency')}
                                                </Typography>
                                            ),
                                        }}
                                        sx={amountFieldStyles}
                                    />
                                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                        {t("A fee of 2 Saudi Riyals will be deducted from the withdrawal amount for the transaction.")}
                                    </Typography>
                                </Stack>
                            </Paper>
                        }

                        {
                            (!hasBankAccount && !isLoading) &&
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2.25, sm: 2.75 },
                                    borderRadius: 3,
                                    border: `1px dashed ${theme.palette.divider}`,
                                }}
                            >
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                display: 'grid',
                                                placeItems: 'center',
                                                backgroundColor: theme.palette.action.hover,
                                            }}
                                        >
                                            <AccountBalanceOutlined />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" fontWeight={800}>
                                                {t("Withdraw")}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {t("A bank account is required for the transfer.")}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box display="flex" justifyContent="flex-start">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<EditOutlined />}
                                            onClick={() => setOpenBankDialog(true)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {t("Edit Bank Account")}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Paper>
                        }

                        {requestingWithdrawalSuccess && (
                            <Alert
                                severity="success"
                                sx={{ borderRadius: 2.5 }}
                            >
                                {t("Done successfully")}
                            </Alert>
                        )}

                        {!!requestingWithdrawalError && (
                            <Alert
                                severity="error"
                                sx={{ borderRadius: 2.5 }}
                            >
                                {requestingWithdrawalError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2.25, sm: 3 },
                        py: 2.25,
                        gap: 1.25,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        flexWrap: 'wrap',
                    }}
                >
                    <LoadingButton
                        onClick={onClose}
                        variant="text"
                        sx={{ textTransform: 'none' }}
                        loading={requestingWithdrawalLoading}
                        disabled={requestingWithdrawalLoading}
                    >
                        {t("Close")}
                    </LoadingButton>

                    <Box flexGrow={1} />

                    <LoadingButton
                        onClick={onConfirm}
                        variant="contained"
                        startIcon={<LocalAtm />}
                        sx={{ textTransform: 'none' }}
                        loading={requestingWithdrawalLoading}
                        disabled={confirmDisabled}
                    >
                        {t("Confirm")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
